import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { researchosService } from '../../services/researchosService';

// Superuser-only browser for every ingested transcript. Loads lightweight METADATA and
// builds a Channel → Year → Month → transcript tree client-side; the full transcript text
// is fetched only when a row is opened (see openDoc). Part of ResearchOS.

const GOLD = 'rgb(var(--brand-gold-rgb))';
const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toISOString().slice(0, 10); } catch { return s; }
}
function hms(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${h}:${m}:${ss}`;
}

const statusBadge = (st) => ({
  new: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  extracting: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  extracted: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  failed_extract: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  skipped: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
}[st] || 'bg-gray-100 text-gray-600');

function Chevron({ open }) {
  return (
    <span className="inline-block w-3 text-gray-400 transition-transform" style={{ transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
  );
}

function SectorChips({ sectors }) {
  if (!sectors || !sectors.length) {
    return <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">neutral</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {sectors.map((s) => (
        <span key={s} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">{s}</span>
      ))}
    </span>
  );
}

/* ── transcript text builders (shared by on-screen view + download) ───────── */
function slugify(s) {
  return (s || 'transcript').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'transcript';
}
function cleanText(doc) {
  return `${doc.title || ''}\n${doc.url || ''}\npublished ${fmtDate(doc.published_at)}\n\n${doc.raw_text || ''}\n`;
}
function timestampedText(doc) {
  const segs = Array.isArray(doc.segments) ? doc.segments : [];
  const body = segs.length
    ? segs.map((s) => `[${hms(s.start)}] ${s.text || ''}`).join('\n')
    : (doc.raw_text || '');
  return `${doc.title || ''}\n${doc.url || ''}\n\n${body}\n`;
}
function downloadText(name, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── Full-transcript modal (lazy) ─────────────────────────────────────────── */
function DocModal({ id, onClose }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('clean');   // 'clean' (no timestamps) | 'timestamped'

  useEffect(() => {
    let alive = true;
    researchosService.getIngestDocument(id)
      .then((r) => { if (alive) setDoc(r.data); })
      .catch(() => { if (alive) setErr('Failed to load transcript.'); });
    return () => { alive = false; };
  }, [id]);

  const hasSegments = doc && Array.isArray(doc.segments) && doc.segments.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-2 sm:p-6" onClick={onClose}>
      <div
        className="relative w-full max-w-3xl rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <div className="min-w-0">
            <h3 className="truncate font-serif text-base text-gray-900 dark:text-gray-100">{doc?.title || (err ? 'Error' : 'Loading…')}</h3>
            {doc && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {doc.source_ref} · {fmtDate(doc.published_at)} · {doc.source_type}
                {doc.url && <> · <a href={doc.url} target="_blank" rel="noreferrer" style={{ color: GOLD }}>source ↗</a></>}
              </p>
            )}
          </div>
          <button className="shrink-0 rounded px-2 py-1 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200" onClick={onClose}>✕</button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-4 scrollbar-thin">
          {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
          {!doc && !err && <p className="text-sm text-gray-500 dark:text-gray-400">Loading transcript…</p>}
          {doc && (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                <SectorChips sectors={doc.sectors} />
                <span className={`rounded px-1.5 py-0.5 ${statusBadge(doc.status)}`}>{doc.status}</span>
                <span className="text-gray-400">{(doc.claims || []).length} claims · {(doc.raw_text || '').length.toLocaleString()} chars</span>
              </div>

              {/* View toggle + downloads */}
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <div className="inline-flex overflow-hidden rounded border border-gray-300 dark:border-gray-700">
                  {[['clean', 'Clean'], ['timestamped', 'Timestamped']].map(([v, label]) => (
                    <button
                      key={v}
                      onClick={() => setView(v)}
                      disabled={v === 'timestamped' && !hasSegments}
                      className="px-2.5 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-40"
                      style={view === v
                        ? { background: GOLD, color: '#1a1a1a', fontWeight: 500 }
                        : { color: '#6b7280' }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <span className="text-[11px] text-gray-400">
                  {view === 'clean' ? 'prose only — feed this to an LLM' : 'one line per caption cue'}
                </span>
                <span className="ml-auto flex items-center gap-1.5">
                  <button
                    onClick={() => downloadText(`${slugify(doc.title)}_clean.txt`, cleanText(doc))}
                    className="rounded px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/40"
                    style={{ border: '1px solid #d1d5db' }}
                  >↓ Clean .txt</button>
                  <button
                    onClick={() => downloadText(`${slugify(doc.title)}_timestamped.txt`, timestampedText(doc))}
                    disabled={!hasSegments}
                    className="rounded px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-800/40"
                    style={{ border: '1px solid #d1d5db' }}
                  >↓ Timestamped .txt</button>
                </span>
              </div>

              {(doc.claims || []).length > 0 && (
                <div className="mb-4 rounded border border-gray-100 p-3 dark:border-gray-800">
                  <div className="mb-1 text-[11px] uppercase tracking-wider text-gray-500">Extracted claims</div>
                  <ul className="space-y-1">
                    {doc.claims.map((c) => (
                      <li key={c.id} className="text-sm text-gray-700 dark:text-gray-300">
                        {c.ticker && <span className="font-mono text-gray-900 dark:text-gray-100">{c.ticker}</span>}{' '}
                        {c.entity && <span className="text-gray-500">({c.entity})</span>}{' '}
                        {c.claim}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {view === 'timestamped' && hasSegments ? (
                <div className="space-y-1 font-sans text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  {doc.segments.map((s, i) => (
                    <p key={i}>
                      <span className="mr-2 select-none font-mono text-[11px] text-gray-400">[{hms(s.start)}]</span>
                      {s.text}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">{doc.raw_text}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Tree ─────────────────────────────────────────────────────────────────── */
// sourceType: 'youtube' | 'x' — scopes the browser to the page's own source (X: one document per handle-day).
export default function TranscriptBrowser({ sourceType } = {}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await researchosService.getIngestDocuments(sourceType ? { source_type: sourceType } : {});
      setDocs(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load transcripts.');
    } finally {
      setLoading(false);
    }
  }, [sourceType]);
  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter((d) =>
      (d.title || '').toLowerCase().includes(needle) ||
      (d.source_ref || '').toLowerCase().includes(needle) ||
      (d.sectors || []).some((s) => s.toLowerCase().includes(needle)));
  }, [docs, q]);

  // Channel → Year → Month → docs. Group by the IMMUTABLE channel_id (fallback: source_ref for
  // legacy docs) so a channel re-run under a different name/@handle stays ONE row. The display
  // label is the most common source_ref seen for that channel_id.
  const tree = useMemo(() => {
    const t = {};
    for (const d of filtered) {
      const key = d.channel_id || d.source_ref || '(no channel)';
      const name = d.source_ref || d.channel_id || '(no channel)';
      const dt = d.published_at ? new Date(d.published_at) : null;
      const y = dt ? String(dt.getUTCFullYear()) : '—';
      const m = dt ? dt.getUTCMonth() + 1 : 0;
      const node = (t[key] ??= { name, count: 0, names: {}, years: {} });
      node.count++;
      node.names[name] = (node.names[name] || 0) + 1;
      const Y = (node.years[y] ??= { count: 0, months: {} }); Y.count++;
      const M = (Y.months[m] ??= { count: 0, docs: [] }); M.count++; M.docs.push(d);
    }
    for (const k of Object.keys(t)) {
      const names = t[k].names;
      t[k].name = Object.keys(names).reduce((a, b) => (names[b] > (names[a] || 0) ? b : a), t[k].name);
    }
    return t;
  }, [filtered]);

  const channels = useMemo(
    () => Object.keys(tree).sort((a, b) => tree[a].name.localeCompare(tree[b].name)), [tree]);

  if (loading) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading transcripts…</p>;
  if (err) return <p className="text-sm text-red-600 dark:text-red-400">{err}</p>;
  if (!docs.length) return <p className="text-sm text-gray-500 dark:text-gray-400">No transcripts ingested yet.</p>;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title / channel / sector…"
          className="w-full max-w-xs rounded border border-gray-300 bg-transparent px-3 py-1.5 text-sm dark:border-gray-700"
        />
        <span className="text-xs text-gray-400">{filtered.length} of {docs.length} transcripts · {channels.length} channels</span>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-800">
        {channels.map((ch) => {
          const chOpen = expanded.has(ch);
          const years = Object.keys(tree[ch].years).sort((a, b) => b.localeCompare(a));
          return (
            <div key={ch} className="border-b border-gray-100 last:border-b-0 dark:border-gray-800/60">
              <button
                className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40"
                onClick={() => toggle(ch)}
              >
                <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  <Chevron open={chOpen} /> {tree[ch].name}
                </span>
                <span className="text-xs text-gray-400">{tree[ch].count}</span>
              </button>

              {chOpen && years.map((y) => {
                const yKey = `${ch}|${y}`;
                const yOpen = expanded.has(yKey);
                const months = Object.keys(tree[ch].years[y].months).map(Number).sort((a, b) => b - a);
                return (
                  <div key={yKey} className="pl-4">
                    <button
                      className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40"
                      onClick={() => toggle(yKey)}
                    >
                      <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"><Chevron open={yOpen} /> {y}</span>
                      <span className="text-xs text-gray-400">{tree[ch].years[y].count}</span>
                    </button>

                    {yOpen && months.map((m) => {
                      const mKey = `${yKey}|${m}`;
                      const mOpen = expanded.has(mKey);
                      const node = tree[ch].years[y].months[m];
                      const rows = [...node.docs].sort((a, b) => String(b.published_at || '').localeCompare(String(a.published_at || '')));
                      return (
                        <div key={mKey} className="pl-4">
                          <button
                            className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40"
                            onClick={() => toggle(mKey)}
                          >
                            <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"><Chevron open={mOpen} /> {MONTHS[m] || '—'}</span>
                            <span className="text-xs text-gray-400">{node.count}</span>
                          </button>

                          {mOpen && (
                            <ul className="pl-6">
                              {rows.map((d) => (
                                <li key={d.id}>
                                  <button
                                    className="flex w-full items-center justify-between gap-3 border-b border-gray-50 px-3 py-2 text-left hover:bg-gray-50 dark:border-gray-800/40 dark:hover:bg-gray-800/40"
                                    onClick={() => setOpenId(d.id)}
                                  >
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate text-sm text-gray-800 dark:text-gray-200">{d.title || d.source_type}</span>
                                      <span className="mt-0.5 flex items-center gap-2">
                                        <span className="text-xs tabular-nums text-gray-400">{fmtDate(d.published_at)}</span>
                                        <SectorChips sectors={d.sectors} />
                                      </span>
                                    </span>
                                    <span className="flex shrink-0 items-center gap-2">
                                      {d.n_claims > 0 && <span className="text-xs text-gray-400">{d.n_claims} claims</span>}
                                      <span className={`rounded px-1.5 py-0.5 text-[10px] ${statusBadge(d.status)}`}>{d.status}</span>
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {openId != null && <DocModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
